import { rpcClient, type RPCMutationInput, type RPCMutationResult, type RPCQueryResult } from "eddev/hooks"
import { reaction } from "mobx"
import { onSnapshot, types } from "mobx-state-tree"
import { safeStorage } from "@utils/safe-storage"
import { authStore } from "./tess-session.store"

const STORAGE_KEY = "carriageworks:tess-cart"
const LEGACY_AUTH_STORAGE_KEY = "carriageworks:tess-auth"

type CartSummary = RPCQueryResult<"api.public.tess.cart.summary">
type CartData = CartSummary["cart"]
type AddToCartInput = Omit<RPCMutationInput<"api.public.tess.cart.addToCart">, "encryptedSessionKey">
type RemoveLineItemInput = Omit<RPCMutationInput<"api.public.tess.cart.removeLineItem">, "encryptedSessionKey">
type CartMutationSummary =
  | RPCMutationResult<"api.public.tess.cart.addToCart">
  | RPCMutationResult<"api.public.tess.cart.removeLineItem">

type StoredCartState = {
  data?: CartData | null
  expirationDate?: Date | string
}

type LegacyAuthState = {
  cart?: CartData | null
  cartExpirationDate?: Date | string
}

const StoreStatus = types.enumeration("TessCartStoreStatus", ["idle", "loading", "loaded", "refreshing", "error"])

const TessCartStore = types
  .model("TessCartStore", {
    data: types.frozen<CartData | null>(null),
    expirationDate: types.maybe(types.Date),
    lastError: types.maybeNull(types.string),
    status: types.optional(StoreStatus, "idle"),
  })
  .views((self) => ({
    get hasItems() {
      return (self.data?.itemCount ?? 0) > 0
    },
    get itemCount() {
      return self.data?.itemCount ?? 0
    },
    get isPending() {
      return self.status === "loading" || self.status === "refreshing"
    },
  }))
  .actions((self) => ({
    applySummary(summary: CartSummary | CartMutationSummary) {
      self.data = summary.cart
      self.expirationDate = getDate(summary.expirationDate)
      self.lastError = null
      self.status = "loaded"
    },
    clear() {
      self.data = null
      self.expirationDate = undefined
      self.lastError = null
      self.status = "idle"
    },
    setError(error: string) {
      self.lastError = error
      self.status = "error"
    },
    setPending() {
      self.lastError = null
      self.status = self.data ? "refreshing" : "loading"
    },
  }))
  .actions((self) => ({
    load() {
      const stored = safeStorage.getData<StoredCartState>(STORAGE_KEY)
      const data = stored?.data ?? null

      self.data = data
      self.expirationDate = getDate(stored?.expirationDate)
      self.status = data ? "loaded" : "idle"
    },
    save() {
      if (!self.data && !self.expirationDate) {
        safeStorage.removeItem(STORAGE_KEY)
        return
      }

      safeStorage.setData<StoredCartState>(STORAGE_KEY, {
        data: self.data,
        expirationDate: self.expirationDate,
      })
    },
  }))
  .actions((self) => ({
    async refresh(): Promise<Result<CartSummary>> {
      self.setPending()

      try {
        const encryptedSessionKey = await authStore.ensureEncryptedSessionKey()
        const summary = await rpcClient.query("api.public.tess.cart.summary", { encryptedSessionKey })
        self.applySummary(summary)
        return { success: true, value: summary }
      } catch (error) {
        const message = getErrorMessage(error)
        self.setError(message)
        return { success: false, error: message }
      }
    },
    async addToCart(input: AddToCartInput): Promise<Result<RPCMutationResult<"api.public.tess.cart.addToCart">>> {
      self.setPending()

      try {
        const encryptedSessionKey = await authStore.ensureEncryptedSessionKey()
        const summary = await rpcClient.mutation("api.public.tess.cart.addToCart", {
          ...input,
          encryptedSessionKey,
        })
        self.applySummary(summary)
        return { success: true, value: summary }
      } catch (error) {
        const message = getErrorMessage(error)
        self.setError(message)
        return { success: false, error: message }
      }
    },
    async removeLineItem(
      input: RemoveLineItemInput,
    ): Promise<Result<RPCMutationResult<"api.public.tess.cart.removeLineItem">>> {
      self.setPending()

      try {
        const encryptedSessionKey = await authStore.ensureEncryptedSessionKey()
        const summary = await rpcClient.mutation("api.public.tess.cart.removeLineItem", {
          ...input,
          encryptedSessionKey,
        })
        self.applySummary(summary)
        return { success: true, value: summary }
      } catch (error) {
        const message = getErrorMessage(error)
        self.setError(message)
        return { success: false, error: message }
      }
    },
  }))

type Result<TValue = undefined, TError = string> =
  | {
      success: true
      value: TValue
    }
  | {
      success: false
      error: TError
    }

function getDate(value: Date | string | undefined) {
  if (!value) {
    return undefined
  }

  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? undefined : date
}

function getErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : String(error)
}

export const cartStore = TessCartStore.create({
  data: null,
})

if (env.client) {
  cartStore.load()
  onSnapshot(cartStore, () => {
    cartStore.save()
  })

  reaction(
    () => [authStore.encryptedSessionKey, authStore.session?.isLoggedIn, authStore.session?.orderId] as const,
    ([encryptedSessionKey]) => {
      if (!encryptedSessionKey) {
        cartStore.clear()
        return
      }

      cartStore.refresh()
    },
  )

  if (authStore.encryptedSessionKey) {
    cartStore.refresh()
  }

  // @ts-ignore
  window.cartStore = cartStore
}
